import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { acquireHighlight } from '../api';
import { usePollJob } from '../hooks/usePollJob';
import { deriveButtonState } from '../lib/deriveButtonState';
import {
  mapAcquireError,
  NETWORK_ACQUIRE_ERROR_COPY,
} from '../lib/mapAcquireError';
import {
  INITIAL_REEL_FLOW_STATE,
  reelFlowReducer,
} from '../lib/reelFlowReducer';
import type {
  RttsAcquireErrorCode,
  RttsGateFields,
  RttsUserRole,
} from '../types';
import InlineReelPanel from './InlineReelPanel';
import ReelProgress from './ReelProgress';
import UpgradeSurface from './UpgradeSurface';

export type RunTheTapeButtonProps = {
  leagueId: string;
  gates: RttsGateFields;
  userRole: RttsUserRole;
  /**
   * Called after a successful acquire with the new job id.
   * Progress polling is owned by this component.
   */
  onJobStarted?: (jobId: string) => void;
  className?: string;
};

function applyAcquireGateCorrection(
  gates: RttsGateFields,
  error: RttsAcquireErrorCode
): RttsGateFields {
  if (error === 'not_entitled') {
    return { ...gates, rtts_entitled: false };
  }
  if (error === 'not_broadcaster') {
    return { ...gates, can_generate: false };
  }
  return gates;
}

const RunTheTapeButton: React.FC<RunTheTapeButtonProps> = ({
  leagueId,
  gates: initialGates,
  userRole,
  onJobStarted,
  className,
}) => {
  const [gates, setGates] = useState(initialGates);
  const [flow, dispatch] = useReducer(reelFlowReducer, INITIAL_REEL_FLOW_STATE);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);
  const [quietToast, setQuietToast] = useState<string | null>(null);

  React.useEffect(() => {
    setGates(initialGates);
    dispatch({ type: 'RESET' });
    setErrorMessage(null);
    setShowRetry(false);
    setQuietToast(null);
  }, [initialGates]);

  const buttonState = useMemo(
    () => deriveButtonState(gates, userRole),
    [gates, userRole]
  );

  const poll = usePollJob({
    leagueId,
    jobId: flow.jobId,
    enabled: flow.phase === 'polling',
  });

  useEffect(() => {
    if (flow.phase !== 'polling' || !poll.job) return;
    const status = String(poll.job.status);
    if (status === 'delivered') dispatch({ type: 'POLL_DELIVERED' });
    if (status === 'failed') dispatch({ type: 'POLL_FAILED' });
  }, [flow.phase, poll.job]);

  const runAcquire = async () => {
    if (flow.phase === 'starting') return;
    // Re-run / force regenerate is removed from the product surface.
    // // BACKEND-DEP: backend should reject force=true / re-acquire when existing_reel is present
    dispatch({ type: 'ACQUIRE_START', force: false });
    setErrorMessage(null);
    setShowRetry(false);
    setQuietToast(null);
    try {
      const result = await acquireHighlight(leagueId, {
        archive_id: gates.archive_id,
        force: false,
      });
      if (result.success) {
        onJobStarted?.(result.job.id);
        dispatch({ type: 'ACQUIRE_SUCCESS', jobId: result.job.id });
        return;
      }
      const mapped = mapAcquireError(result);
      if (mapped.quiet) {
        setQuietToast(mapped.message);
        dispatch({ type: 'ACQUIRE_FAILURE' });
        return;
      }
      setGates((current) => applyAcquireGateCorrection(current, result.error));
      setErrorMessage(mapped.message);
      setShowRetry(mapped.showRetry);
      dispatch({ type: 'ACQUIRE_FAILURE' });
    } catch {
      // Network / unexpected throw / 5xx from live client.
      setShowRetry(true);
      setErrorMessage(NETWORK_ACQUIRE_ERROR_COPY);
      dispatch({ type: 'ACQUIRE_FAILURE' });
    }
  };

  const inFlight = flow.phase === 'starting';

  if (flow.phase === 'starting') {
    return (
      <div className={className} data-testid="rtts-starting">
        <p className="text-xs text-secondary-300">Starting your reel…</p>
      </div>
    );
  }

  if (flow.phase === 'polling') {
    return (
      <div className={className}>
        <ReelProgress
          status={poll.status || 'downloading'}
          errorCode={poll.job?.error_code}
          pollError={poll.error}
        />
      </div>
    );
  }

  if (flow.phase === 'failed') {
    return (
      <div className={className}>
        <ReelProgress
          status="failed"
          errorCode={poll.job?.error_code}
          pollError={poll.error}
          onRetry={() => {
            void runAcquire();
          }}
        />
      </div>
    );
  }

  if (flow.phase === 'delivered' && flow.jobId) {
    return (
      <div className={className} data-testid="rtts-delivered-surface">
        <InlineReelPanel
          leagueId={leagueId}
          gates={{
            ...gates,
            existing_reel: gates.existing_reel ?? {
              job_id: flow.jobId,
              status: 'delivered',
            },
          }}
          initiallyExpanded
        />
        {quietToast ? (
          <p className="mt-1 text-xs text-secondary-400" data-testid="rtts-quiet-toast">
            {quietToast}
          </p>
        ) : null}
      </div>
    );
  }

  const errorBlock =
    errorMessage || showRetry || quietToast ? (
      <div className="mt-1 space-y-1" data-testid="rtts-acquire-error">
        {errorMessage ? (
          <p className="text-xs text-red-300" data-testid="rtts-acquire-error-message">
            {errorMessage}
          </p>
        ) : null}
        {quietToast ? (
          <p className="text-xs text-secondary-400" data-testid="rtts-quiet-toast">
            {quietToast}
          </p>
        ) : null}
        {showRetry ? (
          <button
            type="button"
            className="min-h-[44px] text-xs text-primary-300 hover:text-primary-200 underline"
            data-testid="rtts-acquire-retry"
            disabled={inFlight}
            onClick={() => void runAcquire()}
          >
            Retry
          </button>
        ) : null}
      </div>
    ) : null;

  if (buttonState === 'MEMBER_LOCKED') {
    return (
      <div className={className} data-testid="rtts-member-locked">
        <p className="text-xs text-secondary-500">
          Ask your commissioner about Scoutlytics Studio.
        </p>
        {errorBlock}
      </div>
    );
  }

  if (buttonState === 'UPGRADE') {
    return (
      <div className={className} data-testid="rtts-upgrade-state">
        <UpgradeSurface />
        {errorBlock}
      </div>
    );
  }

  if (buttonState === 'PASSIVE_HINT') {
    return (
      <div className={className} data-testid="rtts-passive-hint">
        <p className="text-xs text-secondary-500">
          The broadcaster can generate a reel.
        </p>
        {errorBlock}
      </div>
    );
  }

  if (buttonState === 'WATCH') {
    return (
      <div className={['w-full max-w-full', className].filter(Boolean).join(' ')} data-testid="rtts-watch-state">
        <InlineReelPanel leagueId={leagueId} gates={gates} />
        {errorBlock}
      </div>
    );
  }

  return (
    <div className={className} data-testid="rtts-generate-state">
      <button
        type="button"
        className="btn-primary text-xs min-h-[44px] disabled:opacity-50"
        data-testid="rtts-generate"
        disabled={inFlight}
        onClick={() => void runAcquire()}
      >
        Make a reel
      </button>
      {errorBlock}
    </div>
  );
};

export default RunTheTapeButton;
